import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { AppConfig } from "../../app-config";
import { CreateModelDataType, DTOSourceFilePartial, Mapping, Pif } from "@pienso/ingest-types/pif.interface";
import { Observable } from "rxjs";
import { flatMap, map, take, tap } from "rxjs/operators";
import { saveAs } from "file-saver";
import { TaskDataService } from "./task-data.service";
import { StatusResponse, TaskResponse } from "@pienso/ingest-types/task.interface";

export interface PifPayload {
  pifName: string;
  contentCol: string;
  mapping: Mapping;
}

export interface CreatePifPayload extends PifPayload {
  corpusId: number;
}

@Injectable({ providedIn: "root" })
export class PifDataService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);
  private taskService = inject(TaskDataService);

  public static hydratePif(pif: Pif) {
    pif.createdAt = pif.createdAt ? new Date(pif.createdAt) : pif.createdAt;
    pif.updatedAt = pif.updatedAt ? new Date(pif.updatedAt) : pif.updatedAt;
    return pif;
  }

  private endpoint: string;

  constructor() {
    const config = this.config;

    this.endpoint = config.ingestApiEndpoint + "/pif/";
  }

  httpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
  }

  getById(pifId: number): Observable<Pif> {
    return this.http
      .get<Pif>(this.endpoint + pifId, this.httpOptions())
      .pipe(map((pif) => PifDataService.hydratePif(pif)));
  }

  defaultMapping(corpusId: number): Observable<Mapping> {
    return this.http.get<Mapping>(this.endpoint + "defaultMapping/" + corpusId, this.httpOptions());
  }

  create(corpusId: number, payload: CreatePifPayload): Observable<Pif> {
    return this.http
      .post<Pif>(this.endpoint, payload, this.httpOptions())
      .pipe(map((pif) => PifDataService.hydratePif(pif)));
  }

  update(pifId: number, payload: PifPayload): Observable<Pif> {
    return this.http
      .put<Pif>(this.endpoint + pifId, payload, this.httpOptions())
      .pipe(map((pif) => PifDataService.hydratePif(pif)));
  }

  download(pif: Pif): Observable<HttpResponse<Blob>> {
    let filename = "";
    return this.http.get<TaskResponse>(this.endpoint + pif.id + "/download", this.httpOptions()).pipe(
      this.taskService.pollTask("Downloading Pienso data for id " + pif.id),
      flatMap((sr: StatusResponse) => {
        filename = sr.details.filename;
        const params = new HttpParams().set("key", sr.details.key).set("filename", filename);
        return this.http.get(this.endpoint + "download", {
          observe: "response",
          responseType: "blob",
          params,
        });
      }),
      tap((response: HttpResponse<any>) => {
        if (response.ok) {
          if (!filename || !filename.trim().length) {
            filename = "pienso_data";
          }
          filename = filename.trim() + ".tsv";
          saveAs(response.body, filename);
        }
      }),
    );
  }

  sendPifToCore(pif: Pif, type: CreateModelDataType): Observable<DTOSourceFilePartial> {
    return this.http.get<TaskResponse>(this.endpoint + pif.id + "/core?type=" + type, this.httpOptions()).pipe(
      this.taskService.pollTask(`Sending data set ${pif.id} to Core / ${type}`),
      map((sr: StatusResponse) => sr.details as DTOSourceFilePartial),
    );
  }

  sendPifToDashboard(pif: Pif): Observable<{ fileKey: string }> {
    return this.http.get<TaskResponse>(this.endpoint + pif.id + "/dashboard", this.httpOptions()).pipe(
      this.taskService.pollTask(`Sending data set ${pif.id} to Dashboard`),
      map((sr: StatusResponse) => sr.details),
    );
  }

  sendPifToExplore(pif: Pif): Observable<{ fileKey: string }> {
    return this.http.post<TaskResponse>(this.endpoint + pif.id + "/explore", this.httpOptions()).pipe(
      this.taskService.pollTask(`Sending data set ${pif.id} to Explore`),
      map((sr: StatusResponse) => sr.details),
    );
  }
}
